import * as i0 from '@angular/core';
import { Component, ViewChildren, Input, NgModule } from '@angular/core';
import * as i2 from '@angular/forms';
import { FormControl, FormArray, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';

class BinInput {
    constructor() {
        this.variantConfig = {
            first4: {
                inputsConfig: [
                    { length: 4 },
                    { disabled: true },
                    { disabled: true },
                    { disabled: true },
                ],
                firstFocussableIndex: 0,
                lastFocussableIndex: 0,
            },
            first6: {
                inputsConfig: [
                    { length: 4 },
                    { length: 2 },
                    { disabled: true },
                    { disabled: true },
                ],
                firstFocussableIndex: 0,
                lastFocussableIndex: 1,
            },
            first8: {
                inputsConfig: [
                    { length: 4 },
                    { length: 4 },
                    { disabled: true },
                    { disabled: true },
                ],
                firstFocussableIndex: 0,
                lastFocussableIndex: 1,
            },
            first9: {
                inputsConfig: [
                    { length: 4 },
                    { length: 4 },
                    { length: 1 },
                    { disabled: true },
                ],
                firstFocussableIndex: 0,
                lastFocussableIndex: 2,
            },
            first4_last4: {
                inputsConfig: [
                    { length: 4 },
                    { disabled: true },
                    { disabled: true },
                    { length: 4 },
                ],
                firstFocussableIndex: 0,
                lastFocussableIndex: 3,
            },
            first6_last4: {
                inputsConfig: [
                    { length: 4 },
                    { length: 2 },
                    { disabled: true },
                    { length: 4 },
                ],
                firstFocussableIndex: 0,
                lastFocussableIndex: 3,
            },
            last4: {
                inputsConfig: [
                    { disabled: true },
                    { disabled: true },
                    { disabled: true },
                    { length: 4 },
                ],
                firstFocussableIndex: 3,
                lastFocussableIndex: 3,
            },
            last8: {
                inputsConfig: [
                    { disabled: true },
                    { disabled: true },
                    { length: 4 },
                    { length: 4 },
                ],
                firstFocussableIndex: 2,
                lastFocussableIndex: 3,
            },
        };
        this.TOTAL_INPUT_BOXES = 4;
        this.DEFAULT_BIN_VARIANT = 'first8';
        this.currentVariant = this.DEFAULT_BIN_VARIANT;
        this.inputs = this.getFormArray(this.TOTAL_INPUT_BOXES, this.currentVariant);
        // there is a way to do this without maintaining a variable for focus
        // but safari has an issue where the order of events (keydown, input, keyup) might get jumbled in some scenarios
        // hence we are sticking with using a variable here for referencing the position to be focussed on
        this.#scheduledFocus = null;
        this.label = '';
        this.separator = '';
        this.isInvalid = false;
        this.errorMessage = '';
        this.style = {};
    }
    // there is a way to do this without maintaining a variable for focus
    // but safari has an issue where the order of events (keydown, input, keyup) might get jumbled in some scenarios
    // hence we are sticking with using a variable here for referencing the position to be focussed on
    #scheduledFocus;
    set variant(value) {
        this.currentVariant = value;
        this.inputs = this.getFormArray(this.TOTAL_INPUT_BOXES, this.currentVariant);
    }
    handleInput() {
        this.#updateWiredValue();
        if (this.#scheduledFocus !== null) {
            this.#focusInput(this.#scheduledFocus);
            this.#scheduledFocus = null;
        }
    }
    handleFocus(e) {
        e.target.focus();
    }
    handleKeyDown(event, index) {
        const currentVariantConfig = this.variantConfig[this.currentVariant];
        // allow tabs to switch between inputs
        if (event.key === 'Tab') {
            return;
        }
        if (event.key === 'Backspace' || event.key === 'Delete') {
            // In case the input is already filled, delete the value and focus on the same input only
            if (this.inputs.controls[index].value) {
                this.#scheduledFocus = index;
            }
            else {
                // In case of delete or backspace queue up the focus to be done on previous index if the current input isn't filled
                if (index > currentVariantConfig.firstFocussableIndex) {
                    this.#focusInput(this.findNextFocusIndex(index, true));
                }
            }
            return;
        }
        const isDigit = /\d/.test(event.key);
        // Safari fires Cmd + V through keyPress event as well
        // So we need to handle it here and let it through, this is then handled by the paste handler
        if (event.key === 'v' && event.metaKey) {
            // TODO - should we handle paste?
            // currently skipping it, not a p0, will understand the priority and include this in a separate PR
            event.preventDefault();
            return;
        }
        // return if a non-numeric and unallowed character is pressed
        if (!isDigit) {
            event.preventDefault();
            return;
        }
        if (isDigit && this.inputs.controls[index].value) {
            if (this.inputs.controls[index].value.length >=
                (currentVariantConfig.inputsConfig[index].length ?? 0)) {
                // If input box already has maximum allowed characters, don't allow user input and focus on next input box
                // only do this if current index is not the last focussable index
                event.preventDefault();
                if (index !== currentVariantConfig.lastFocussableIndex) {
                    this.#focusInput(this.findNextFocusIndex(index));
                }
            }
            else if (this.inputs.controls[index].value.length ===
                (currentVariantConfig.inputsConfig[index].length ?? 0) - 1) {
                // After input of current character, focus should shift to the next allowable input
                // Allow user to give input and schedule the focus to move to the next index when this occurs
                if (index !== currentVariantConfig.lastFocussableIndex) {
                    this.#scheduledFocus = this.findNextFocusIndex(index);
                }
            }
        }
    }
    writeValue(obj) {
        if (obj && obj.bin) {
            const binValue = obj.bin.replaceAll(this.separator, '');
            let currIndex = 0;
            for (let index = 0; index < this.TOTAL_INPUT_BOXES; index++) {
                const currentIndexInputConfig = this.variantConfig[this.currentVariant].inputsConfig[index];
                if (!currentIndexInputConfig.disabled) {
                    this.inputs
                        .at(index)
                        .setValue(binValue.slice(currIndex, currIndex + (currentIndexInputConfig.length ?? 0)));
                    currIndex += currentIndexInputConfig.length ?? 0;
                }
            }
        }
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        // only enable or disable the boxes which can be enabled/disabled as per config
        // those input boxes which always stay disabled will still always remain disabled
        for (let i = 0; i < this.TOTAL_INPUT_BOXES; i++) {
            if (!this.variantConfig[this.currentVariant].inputsConfig[i].disabled) {
                isDisabled ? this.inputs.at(i)?.disable : this.inputs.at(i)?.enable;
            }
        }
    }
    findNextFocusIndex(currentIndex, isBackspace = false) {
        let currInd;
        if (isBackspace) {
            currInd = currentIndex - 1;
            // until the next input box is enabled and within range, keep on going left to find the index of such input box
            while (currInd >= 0 &&
                !!this.variantConfig[this.currentVariant].inputsConfig[currInd].disabled) {
                currInd--;
            }
        }
        else {
            currInd = currentIndex + 1;
            // until the next input box is enabled and within range, keep on going right to find the index of such input box
            while (currInd <= this.TOTAL_INPUT_BOXES - 1 &&
                !!this.variantConfig[this.currentVariant].inputsConfig[currInd].disabled) {
                currInd++;
            }
        }
        return currInd;
    }
    getFormArray(_size, variant) {
        const arr = [];
        for (let i = 0; i < _size; i++) {
            arr.push(new FormControl({
                value: '',
                disabled: this.variantConfig[variant].inputsConfig[i].disabled ?? false,
            }));
        }
        return new FormArray(arr);
    }
    #focusInput(index) {
        // in case focus is asked for overflowing index, ignore the same
        if (index < 0 || index > this.TOTAL_INPUT_BOXES - 1) {
            return;
        }
        // In order not to interfere with the input we setTimeout before advancing the focus
        // Without setTimeout this would create issues
        setTimeout(() => this.inputEls.get(index)?.nativeElement.focus());
    }
    #updateWiredValue() {
        // We want to expose the value as a plain string
        setTimeout(() => this.onChange?.({
            bin: this.inputs.value.join(this.separator),
            binType: this.currentVariant,
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BinInput, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.8", type: BinInput, selector: "pv-bin-input", inputs: { label: "label", variant: "variant", separator: "separator", isInvalid: "isInvalid", errorMessage: "errorMessage", style: "style" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: BinInput,
                multi: true,
            },
        ], viewQueries: [{ propertyName: "inputEls", predicate: ["binInputEl"], descendants: true }], ngImport: i0, template: "<div\n  class=\"pv-bin-input-container flex flex-column gap-1 w-max\"\n  [style]=\"this.style?.container\">\n  <span\n    *ngIf=\"this.label\"\n    class=\"pv-bin-input-label mb-3 surface-text-subtle-low-contrast text-sm\"\n    [style]=\"this.style?.label\">\n    {{ this.label }}\n  </span>\n  <div\n    class=\"pv-bin-input flex flex-row gap-2\"\n    [style]=\"this.style?.inputContainer\">\n    <input\n      #binInputEl\n      [class.is-invalid-bin]=\"\n        !this.variantConfig[this.currentVariant].inputsConfig[i].disabled &&\n        this.isInvalid\n      \"\n      class=\"pv-bin-input-ele text-base w-6rem h-3rem text-left font-bold py-3 px-4\"\n      *ngFor=\"let input of this.inputs.controls; let i = index\"\n      [formControl]=\"$any(input)\"\n      type=\"text\"\n      inputmode=\"numeric\"\n      [placeholder]=\"\n        this.variantConfig[this.currentVariant].inputsConfig[i].disabled\n          ? 'XXXX'\n          : ''\n      \"\n      (input)=\"this.handleInput()\"\n      (keydown)=\"this.handleKeyDown($event, i)\"\n      (focus)=\"this.handleFocus($event)\"\n      [style]=\"this.style?.input\" />\n  </div>\n  <span\n    *ngIf=\"this.isInvalid\"\n    class=\"pv-invalid-bin-label feedback-text-negative w-max text-sm mt-2\"\n    [style]=\"this.style?.error\">\n    {{ this.errorMessage }}\n  </span>\n</div>\n", styles: [".pv-bin-input-ele{border-radius:2px;outline-color:var(--primary-color);border:.6px solid var(--gray-400)}.pv-bin-input-ele.is-invalid-bin{border-color:var(--feedback-background-negative-high-contrast);outline-color:var(--feedback-background-negative-high-contrast)}.pv-bin-input-ele:disabled{font-weight:400}\n"], dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BinInput, decorators: [{
            type: Component,
            args: [{ selector: 'pv-bin-input', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: BinInput,
                            multi: true,
                        },
                    ], template: "<div\n  class=\"pv-bin-input-container flex flex-column gap-1 w-max\"\n  [style]=\"this.style?.container\">\n  <span\n    *ngIf=\"this.label\"\n    class=\"pv-bin-input-label mb-3 surface-text-subtle-low-contrast text-sm\"\n    [style]=\"this.style?.label\">\n    {{ this.label }}\n  </span>\n  <div\n    class=\"pv-bin-input flex flex-row gap-2\"\n    [style]=\"this.style?.inputContainer\">\n    <input\n      #binInputEl\n      [class.is-invalid-bin]=\"\n        !this.variantConfig[this.currentVariant].inputsConfig[i].disabled &&\n        this.isInvalid\n      \"\n      class=\"pv-bin-input-ele text-base w-6rem h-3rem text-left font-bold py-3 px-4\"\n      *ngFor=\"let input of this.inputs.controls; let i = index\"\n      [formControl]=\"$any(input)\"\n      type=\"text\"\n      inputmode=\"numeric\"\n      [placeholder]=\"\n        this.variantConfig[this.currentVariant].inputsConfig[i].disabled\n          ? 'XXXX'\n          : ''\n      \"\n      (input)=\"this.handleInput()\"\n      (keydown)=\"this.handleKeyDown($event, i)\"\n      (focus)=\"this.handleFocus($event)\"\n      [style]=\"this.style?.input\" />\n  </div>\n  <span\n    *ngIf=\"this.isInvalid\"\n    class=\"pv-invalid-bin-label feedback-text-negative w-max text-sm mt-2\"\n    [style]=\"this.style?.error\">\n    {{ this.errorMessage }}\n  </span>\n</div>\n", styles: [".pv-bin-input-ele{border-radius:2px;outline-color:var(--primary-color);border:.6px solid var(--gray-400)}.pv-bin-input-ele.is-invalid-bin{border-color:var(--feedback-background-negative-high-contrast);outline-color:var(--feedback-background-negative-high-contrast)}.pv-bin-input-ele:disabled{font-weight:400}\n"] }]
        }], propDecorators: { inputEls: [{
                type: ViewChildren,
                args: ['binInputEl']
            }], label: [{
                type: Input
            }], variant: [{
                type: Input
            }], separator: [{
                type: Input
            }], isInvalid: [{
                type: Input
            }], errorMessage: [{
                type: Input
            }], style: [{
                type: Input
            }] } });

class PvBinInputModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: PvBinInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.8", ngImport: i0, type: PvBinInputModule, declarations: [BinInput], imports: [CommonModule, ReactiveFormsModule], exports: [BinInput] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: PvBinInputModule, imports: [CommonModule, ReactiveFormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: PvBinInputModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, ReactiveFormsModule],
                    declarations: [BinInput],
                    exports: [BinInput],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { BinInput, PvBinInputModule };
